import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useBilling } from '../hooks/useBilling'
import { useCurrentWorkspace } from '../hooks/useWorkspace'
import { formatCredits } from '../utils/format'

/** Below this many credits the balance reads as low. */
const LOW_CREDITS = 100

/** The account button in the header: the workspace's credits, settings and log out. */
export function AccountMenu({ email, onSignOut, signingOut }: { email: string; onSignOut: () => void; signingOut: boolean }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const { workspace } = useCurrentWorkspace()
  const billing = useBilling(workspace?.id ?? '')
  const available = billing.data?.balance.available
  const low = available !== undefined && available < LOW_CREDITS * 1_000_000

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  return (
    <div className="account-menu" ref={root}>
      <button type="button" className="account-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((now) => !now)}>
        <span className="account-avatar" aria-hidden="true">{email.slice(0, 1).toUpperCase()}</span>
        <span className="truncate">{email}</span>
        <span className="account-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="account-panel" role="menu">
          {available !== undefined && (
            <Link to="/studio/billing" role="menuitem" className="account-credits" onClick={() => setOpen(false)}>
              <span className="muted small">{workspace?.name}</span>
              <strong className={low ? 'warn-text' : undefined}>{formatCredits(available)} credits</strong>
              <span className="account-credits-link">{low ? "Running low: add credits" : "See credits and plans"}</span>
            </Link>
          )}
          <Link to="/studio/workspace" role="menuitem" className="account-item" onClick={() => setOpen(false)}>Workspace settings</Link>
          <button type="button" role="menuitem" className="account-item" disabled={signingOut} onClick={onSignOut}>
            {signingOut ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      )}
    </div>
  )
}
